import type { OCAttachment } from '../types/gateway';
import type { SenderMeta } from './userMessageMeta';
import { buildSenderBlock } from './userMessageMeta';

export interface AgentRoleContext {
  active_role: string;
  role_label: string;
  role_goal: string;
  expected_agent_behavior: string;
}

/**
 * Build params for a chat.send request. When `roleContext` is provided we
 * prepend a fenced metadata block to the message so the agent can adapt its
 * tone and outputs to the active UI role. The block is human-readable but
 * intentionally distinct so the agent (and any log scanners) can spot it.
 *
 * When `sender` is provided we also prepend a [SENDER] block carrying the
 * author's identity + a server countersign. Gateway frames carry no user
 * identity, so this block is how shared-thread history gets attributed —
 * extractUserMessageMeta strips it back out before render.
 */
export function buildChatSend(
  sessionKey: string,
  message: string,
  attachments?: OCAttachment[],
  roleContext?: AgentRoleContext,
  sender?: SenderMeta
) {
  let finalMessage = message;
  if (sender) {
    finalMessage = buildSenderBlock(sender) + finalMessage;
  }
  if (roleContext) {
    finalMessage = `[ROLE_CONTEXT]\n${JSON.stringify(roleContext)}\n[/ROLE_CONTEXT]\n\n${finalMessage}`;
  }

  return {
    sessionKey,
    message: finalMessage,
    deliver: false,
    idempotencyKey: self.crypto?.randomUUID?.() || (Math.random().toString(36) + Date.now().toString(36)),
    attachments: attachments?.length ? attachments : undefined,
  };
}

/**
 * Longest-edge target for uploaded photos. Anthropic's vision pipeline already
 * downsamples images to ~1568px on the long edge, so shrinking to this here
 * costs the model NO detail while turning a 2–4MB phone photo into ~300–600KB.
 * That keeps every image comfortably under the gateway's inline threshold
 * (attachment-normalize OFFLOAD_THRESHOLD_BYTES = 2MB): above it, images are
 * "offloaded" to a text-only media:// reference the model never sees — the exact
 * bug where large job-site photos silently failed to reach Foreman.
 */
const IMAGE_MAX_EDGE = 1568;
const IMAGE_JPEG_QUALITY = 0.85;

function readFileAsBase64(file: File): Promise<{ mimeType: string; content: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const match = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
      if (match) resolve({ mimeType: match[1], content: match[2] });
      else reject(new Error('Failed to encode file as base64'));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Downscale an image File to <= IMAGE_MAX_EDGE on its longest side and re-encode
 * as JPEG. Uses createImageBitmap with imageOrientation:'from-image' so EXIF
 * rotation is baked in (fixes sideways phone photos). Returns the resized base64,
 * or null if the browser can't decode it (e.g. some HEIC) so the caller can fall
 * back to the raw bytes rather than dropping the attachment.
 */
async function downscaleImage(file: File): Promise<{ mimeType: string; content: string } | null> {
  try {
    if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return null;
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const { width, height } = bitmap;
    if (!width || !height) { bitmap.close?.(); return null; }
    const scale = Math.min(1, IMAGE_MAX_EDGE / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) { bitmap.close?.(); return null; }
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const dataUrl = canvas.toDataURL('image/jpeg', IMAGE_JPEG_QUALITY);
    const match = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
    if (!match) return null;
    return { mimeType: match[1], content: match[2] };
  } catch {
    return null; // any decode/encode failure → caller falls back to raw bytes
  }
}

/** Encode a file as an OpenClaw image attachment (photos are downscaled first). */
export async function fileToAttachment(file: File): Promise<OCAttachment> {
  const isImage = file.type.startsWith('image/')
    || /\.(jpe?g|png|heic|heif|webp)$/i.test(file.name);
  if (isImage) {
    const resized = await downscaleImage(file);
    if (resized) {
      return { type: 'image', mimeType: resized.mimeType, content: resized.content, fileName: file.name };
    }
    // fall through to raw encode if the browser couldn't decode the image
  }
  const raw = await readFileAsBase64(file);
  return { type: 'image', mimeType: raw.mimeType, content: raw.content, fileName: file.name };
}

// ── Legacy exports (kept for any remaining imports) ─────────────────────────

/** @deprecated Use gateway.request('chat.send', buildChatSend(...)) */
export function buildMessageSend(content: string, sessionId?: string, attachments?: { name: string; type: string; data: string; size: number }[]) {
  // Convert old attachment format to new
  const ocAttachments: OCAttachment[] | undefined = attachments?.map((a) => ({
    type: 'image' as const,
    mimeType: a.type,
    content: a.data,
  }));
  return buildChatSend(sessionId || 'main', content, ocAttachments);
}

/** @deprecated Use buildChatSend directly */
export function buildSessionCreate(_name?: string) {
  return { type: 'req', method: 'session.create' };
}

/** @deprecated No longer used */
export function buildSessionList() {
  return { type: 'req', method: 'session.list' };
}

/** @deprecated No longer used */
export function buildPresenceUpdate(_status: string, _sessionId?: string) {
  return {};
}
